import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Square, Brain, ChevronDown, Plus, Search, Terminal, Wrench, Check, Paperclip, X, Loader2, FileText, Image } from 'lucide-react';

const MODEL_OPTIONS = [
  { value: 'haiku', label: 'Haiku' },
  { value: 'sonnet', label: 'Sonnet' },
  { value: 'opus', label: 'Opus' },
  { value: 'opus-4-7', label: 'Opus 4.7' },
  { value: 'opus-4-8', label: 'Opus 4.8' },
];

const ACCEPT_TYPES = [
  'image/jpeg', 'image/png', 'image/gif', 'image/webp',
  'application/pdf',
  'text/plain', 'text/html', 'text/markdown', 'text/css', 'text/csv', 'text/xml',
  'text/javascript', 'application/json', 'application/javascript',
  'application/xml', 'application/x-yaml', 'application/yaml',
  // Common code / text extensions as fallbacks (browsers may not map MIME types for all)
  '.txt', '.html', '.htm', '.md', '.css', '.csv', '.xml', '.json', '.js',
  '.ts', '.jsx', '.tsx', '.py', '.yaml', '.yml', '.svg', '.sh', '.sql',
  '.env', '.cfg', '.ini', '.toml', '.log', '.rst', '.tex',
].join(',');

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function ChatInput({
  onSend,
  onStop,
  isStreaming,
  disabled = false,
  model,
  setModel,
  thinkingEnabled,
  setThinkingEnabled,
  enabledTools,
  setEnabledTools,
  onClearChat,
  hasMessages,
  files = [],
  isUploading = false,
  onUploadFile,
  onRemoveFile,
}) {
  const [text, setText] = useState('');
  const [showModelDropdown, setShowModelDropdown] = useState(false);
  const [showToolsDropdown, setShowToolsDropdown] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const dragCounterRef = useRef(0);

  const selectedModel = MODEL_OPTIONS.find(m => m.value === model);
  const isAdaptiveModel = model === 'sonnet' || model === 'opus' || model === 'opus-4-7' || model === 'opus-4-8';

  const toggleTool = (tool) => {
    setEnabledTools(prev =>
      prev.includes(tool) ? prev.filter(t => t !== tool) : [...prev, tool]
    );
  };

  // Auto-focus textarea on new chat
  useEffect(() => {
    if (!hasMessages && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [hasMessages]);

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 150) + 'px';
  }, [text]);

  const handleFiles = useCallback((fileList) => {
    if (!onUploadFile) return;
    for (const file of fileList) {
      onUploadFile(file);
    }
  }, [onUploadFile]);

  const handleSend = () => {
    const trimmed = text.trim();
    if ((!trimmed && files.length === 0) || isStreaming || disabled || isUploading) return;
    onSend(trimmed);
    setText('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (dragCounterRef.current === 1) setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) setIsDragOver(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragOver(false);
    if (e.dataTransfer.files?.length) {
      handleFiles(Array.from(e.dataTransfer.files));
    }
  };

  const hasReadyFiles = files.some(f => f.file_id && !f.uploading && !f.error);
  const canSend = (text.trim() || hasReadyFiles) && !isUploading;

  return (
    <div className="max-w-3xl mx-auto">
      {/* Tools dropdown above textarea */}
      <div className="flex items-center mb-1.5 px-1">
        <div className="relative">
          <button
            onClick={() => setShowToolsDropdown(!showToolsDropdown)}
            className={`flex items-center gap-1.5 px-2 py-0.5 text-xs rounded transition-colors ${
              enabledTools.length < 2
                ? 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
            }`}
          >
            <Wrench className="w-3 h-3" />
            Tools
            {enabledTools.length < 2 && (
              <span className="text-[10px] opacity-60">({enabledTools.length}/2)</span>
            )}
          </button>
          {showToolsDropdown && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowToolsDropdown(false)} />
              <div className="absolute bottom-full left-0 mb-1 w-44 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-20 py-1">
                {[
                  { key: 'web_search', label: 'Web Search', icon: Search },
                  { key: 'code_execution', label: 'Code Execution', icon: Terminal },
                ].map(({ key, label, icon: Icon }) => (
                  <button
                    key={key}
                    onClick={() => toggleTool(key)}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <Icon className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                    <span className="flex-1 text-left">{label}</span>
                    {enabledTools.includes(key) && (
                      <Check className="w-3.5 h-3.5 text-brand-500" />
                    )}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* File chips above textarea */}
      {files.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2 px-1">
          {files.map(f => (
            <div
              key={f.tempId}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs border ${
                f.error
                  ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400'
                  : f.uploading
                  ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
                  : 'border-brand-200 dark:border-brand-800 bg-brand-50 dark:bg-brand-950/30 text-brand-700 dark:text-brand-300'
              }`}
            >
              {f.uploading ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : f.mime_type?.startsWith('image/') ? (
                <Image className="w-3 h-3" />
              ) : (
                <FileText className="w-3 h-3" />
              )}
              <span className="max-w-[120px] truncate">{f.filename}</span>
              {f.size_bytes && !f.error && (
                <span className="opacity-60">{formatFileSize(f.size_bytes)}</span>
              )}
              {f.error && <span className="text-red-500 truncate max-w-[100px]">{f.error}</span>}
              {onRemoveFile && (
                <button
                  onClick={() => onRemoveFile(f.tempId)}
                  className="ml-0.5 hover:text-red-500 transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Textarea with inline send/stop button */}
      <div
        className={`relative rounded-2xl transition-colors ${
          isDragOver ? 'ring-2 ring-brand-500 border-brand-500' : ''
        }`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {isDragOver && (
          <div className="absolute inset-0 rounded-2xl bg-brand-50/80 dark:bg-brand-950/40 z-10 flex items-center justify-center pointer-events-none">
            <span className="text-sm text-brand-600 dark:text-brand-400 font-medium">Drop files here</span>
          </div>
        )}
        <textarea
          ref={textareaRef}
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Send a message..."
          rows={3}
          className="w-full resize-none rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 pl-10 pr-12 py-3 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-shadow"
          disabled={isStreaming || disabled}
        />

        {/* Attach button — inside textarea, bottom-left */}
        <div className="absolute left-2 bottom-2">
          <button
            onClick={() => fileInputRef.current?.click()}
            className="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all"
            title="Attach file"
            disabled={isStreaming || disabled}
          >
            <Paperclip className="w-4 h-4" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT_TYPES}
            multiple
            className="hidden"
            onChange={e => {
              if (e.target.files?.length) {
                handleFiles(Array.from(e.target.files));
                e.target.value = '';
              }
            }}
          />
        </div>

        {/* Send / Stop button — inside textarea, bottom-right */}
        <div className="absolute right-2 bottom-2">
          {isStreaming ? (
            <button
              onClick={onStop}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-400 hover:bg-gray-500 dark:bg-gray-500 dark:hover:bg-gray-400 text-white transition-all"
              title="Stop generating"
            >
              <Square className="w-3.5 h-3.5" />
            </button>
          ) : (
            <button
              onClick={handleSend}
              disabled={!canSend || disabled}
              className={`w-8 h-8 flex items-center justify-center rounded-full bg-brand-600 hover:bg-brand-700 text-white transition-all ${
                canSend ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
              }`}
              title="Send message"
            >
              <Send className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Controls row */}
      <div className="flex items-center gap-2 mt-2 px-1">
        {/* Model selector pill */}
        <div className="relative">
          <button
            onClick={() => setShowModelDropdown(!showModelDropdown)}
            className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            {selectedModel?.label}
            <ChevronDown className="w-3 h-3" />
          </button>
          {showModelDropdown && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowModelDropdown(false)} />
              <div className="absolute bottom-full left-0 mb-1 w-32 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-20 py-1">
                {MODEL_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => { setModel(opt.value); setShowModelDropdown(false); }}
                    className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                      model === opt.value ? 'text-brand-600 dark:text-brand-400 font-medium' : 'text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Thinking toggle pill */}
        {isAdaptiveModel ? (
          <div
            className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-default"
            title="Thinking is always on in adaptive mode for Sonnet and Opus"
          >
            <Brain className="w-3 h-3" />
            Thinking · Adaptive
          </div>
        ) : (
          <button
            onClick={() => setThinkingEnabled(!thinkingEnabled)}
            className={`flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full border transition-colors ${
              thinkingEnabled
                ? 'border-purple-300 dark:border-purple-600 bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
                : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
            title="Toggle extended thinking"
          >
            <Brain className="w-3 h-3" />
            Thinking
          </button>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* New Chat pill */}
        <button
          onClick={onClearChat}
          disabled={!hasMessages}
          className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Plus className="w-3 h-3" />
          New Chat
        </button>
      </div>
    </div>
  );
}
